import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  IconCamera,
  IconPhone,
  IconCheck,
  IconX,
  IconEdit,
  IconCalendar,
  IconSparkles
} from './Icons';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileUpdated?: (updatedUser: any) => void;
}

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({ isOpen, onClose, onProfileUpdated }) => {
  const { user, setUser } = useAuth();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    position: '',
    birthDate: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    bio: '',
    chatStatus: 'disponible',
    avatar: ''
  });

  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      // Cargar datos más recientes del usuario
      api.get('/users/me').then(res => {
        const u = res.data || user;
        setForm({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          position: u.position || '',
          birthDate: u.birthDate ? new Date(u.birthDate).toISOString().split('T')[0] : '',
          address: u.address || '',
          emergencyContactName: u.emergencyContactName || '',
          emergencyContactPhone: u.emergencyContactPhone || '',
          bio: u.bio || '',
          chatStatus: u.chatStatus || 'disponible',
          avatar: u.avatar || ''
        });
      }).catch(() => {
        setForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          position: (user as any).position || '',
          birthDate: (user as any).birthDate ? new Date((user as any).birthDate).toISOString().split('T')[0] : '',
          address: (user as any).address || '',
          emergencyContactName: (user as any).emergencyContactName || '',
          emergencyContactPhone: (user as any).emergencyContactPhone || '',
          bio: (user as any).bio || '',
          chatStatus: (user as any).chatStatus || 'disponible',
          avatar: (user as any).avatar || ''
        });
      });
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Límite de tamaño 10MB
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen debe pesar menos de 10 MB');
      return;
    }

    setUploadingAvatar(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data?.url) {
        setForm(prev => ({ ...prev, avatar: res.data.url }));
        toast.success('Foto cargada correctamente');
      }
    } catch {
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/users/me/profile', form);
      const updatedUser = res.data;

      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('platform_user', JSON.stringify(updatedUser));
        if (updatedUser.avatar) {
          // Avatar vinculado exclusivamente al estado del usuario
        try { localStorage.removeItem('userAvatar'); } catch {}
        }
        if (onProfileUpdated) {
          onProfileUpdated(updatedUser);
        }
      }

      toast.success('¡Tus datos personales se han actualizado correctamente!');
      onClose();
    } catch (err: any) {
      console.error('Error al actualizar perfil:', err);
      toast.error(err.response?.data?.error || 'Error al actualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  // Cálculo de edad si tiene fecha de nacimiento
  let ageString: string | null = null;
  let birthdayCountdown: string | null = null;
  if (form.birthDate) {
    const bdate = new Date(form.birthDate);
    const now = new Date();
    let age = now.getFullYear() - bdate.getFullYear();
    const m = now.getMonth() - bdate.getUTCMonth();
    if (m < 0 || (m === 0 && now.getDate() < bdate.getUTCDate())) {
      age--;
    }
    ageString = `${age} años`;

    const nextBday = new Date(now.getFullYear(), bdate.getUTCMonth(), bdate.getUTCDate());
    if (nextBday.getTime() < now.getTime()) {
      nextBday.setFullYear(now.getFullYear() + 1);
    }
    const days = Math.ceil((nextBday.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0 || (now.getMonth() === bdate.getUTCMonth() && now.getDate() === bdate.getUTCDate())) {
      birthdayCountdown = '🎉 ¡Hoy es tu cumpleaños!';
    } else {
      birthdayCountdown = `🎂 Faltan ${days} días para tu cumpleaños`;
    }
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 42, 74, 0.55)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: '20px'
    }}>
      <div
        className="liquid-glass-modal"
        style={{
          borderRadius: '24px',
          width: '100%',
          maxWidth: '620px',
          maxHeight: '92vh',
          overflowY: 'auto',
          padding: '30px 34px',
          boxSizing: 'border-box'
        }}
      >
        {/* CABECERA */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(15, 42, 74, 0.08)', paddingBottom: '14px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900, color: '#0f2a4a', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IconSparkles size={20} color="#0070f3" />
              <span>Mi Perfil · Datos Personales</span>
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
              Actualiza tu información personal, fecha de nacimiento y contacto de emergencia.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid rgba(15, 42, 74, 0.1)',
              background: 'rgba(255, 255, 255, 0.8)',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          
          {/* FOTO DE PERFIL & ESTADO DE DISPONIBILIDAD */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', background: 'rgba(255, 255, 255, 0.65)', borderRadius: '18px', padding: '16px 20px', border: '1px solid rgba(15, 42, 74, 0.08)' }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '74px',
                height: '74px',
                borderRadius: '22px',
                background: 'linear-gradient(135deg, #0070f3 0%, #005bb5 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: 900,
                overflow: 'hidden',
                border: '2px solid #ffffff',
                boxShadow: '0 6px 18px rgba(0, 112, 243, 0.25)'
              }}>
                {form.avatar ? (
                  <img src={form.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  (form.firstName?.[0] || 'U')
                )}
              </div>

              {/* Botón flotante para cambiar foto */}
              <label
                style={{
                  position: 'absolute',
                  bottom: '-4px',
                  right: '-4px',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#0070f3',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px solid #ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
                title="Cambiar foto de perfil"
              >
                <IconCamera size={14} color="#ffffff" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                  disabled={uploadingAvatar}
                />
              </label>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: 900, color: '#0f2a4a' }}>
                  {form.firstName || user?.firstName} {form.lastName || user?.lastName}
                </span>
                <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '6px', background: 'rgba(0,112,243,0.1)', color: '#0070f3' }}>
                  {user?.role}
                </span>
              </div>
              <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px' }}>
                {user?.email}
              </span>

              {uploadingAvatar && (
                <span style={{ fontSize: '10px', color: '#0070f3', fontWeight: 800, display: 'block', marginTop: '4px' }}>
                  Subiendo nueva foto...
                </span>
              )}

              {/* Selector rápido de estado */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                {[
                  { key: 'disponible', label: 'Disponible', color: '#10b981' },
                  { key: 'ocupado', label: 'Ocupado', color: '#ef4444' },
                  { key: 'reunion', label: 'En reunión', color: '#f59e0b' },
                  { key: 'almuerzo', label: 'En almuerzo', color: '#ec4899' },
                  { key: 'ausente', label: 'Ausente', color: '#8b5cf6' }
                ].map(s => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setForm({ ...form, chatStatus: s.key })}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '6px',
                      border: form.chatStatus === s.key ? `1.5px solid ${s.color}` : '1px solid rgba(15,42,74,0.1)',
                      background: form.chatStatus === s.key ? `${s.color}15` : 'transparent',
                      color: form.chatStatus === s.key ? s.color : '#64748b',
                      fontSize: '10px',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.color }} />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* NOMBRES Y APELLIDOS */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Nombres</label>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={e => setForm({ ...form, firstName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(15,42,74,0.12)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '13px',
                  fontWeight: 600,
                  outline: 'none',
                  fontFamily: '"Montserrat", sans-serif',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Apellidos</label>
              <input
                type="text"
                required
                value={form.lastName}
                onChange={e => setForm({ ...form, lastName: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  borderRadius: '12px',
                  border: '1.5px solid rgba(15,42,74,0.12)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '13px',
                  fontWeight: 600,
                  outline: 'none',
                  fontFamily: '"Montserrat", sans-serif',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          {/* CARGO EN ZOIK */}
          <div>
            <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Cargo / Especialidad en ZOIK</label>
            <input
              type="text"
              placeholder="Ej: Diseñador Gráfico & Multimedia, Editor de Video, Copywriter"
              value={form.position}
              onChange={e => setForm({ ...form, position: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1.5px solid rgba(15,42,74,0.12)',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '13px',
                fontWeight: 600,
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* BLOQUE DESTACADO: DATOS PERSONALES & UBICACIÓN */}
          <div style={{ background: 'rgba(0,112,243,0.04)', borderRadius: '16px', padding: '16px 18px', border: '1.5px solid rgba(0,112,243,0.15)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 900, color: '#0070f3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Datos Personales & Cumpleaños
              </span>
              {birthdayCountdown && (
                <span style={{ fontSize: '11px', fontWeight: 800, color: '#9333ea', background: '#fdf4ff', padding: '2px 8px', borderRadius: '6px', border: '1px solid #f0abfc' }}>
                  {birthdayCountdown}
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.3fr', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                  🎂 Fecha de Nacimiento
                </label>
                <input
                  type="date"
                  value={form.birthDate}
                  onChange={e => setForm({ ...form, birthDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '13px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
                {ageString && (
                  <span style={{ fontSize: '10px', color: '#0070f3', fontWeight: 700, display: 'block', marginTop: '3px' }}>
                    Edad: {ageString}
                  </span>
                )}
              </div>

              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px' }}>
                  📍 Dirección de Residencia
                </label>
                <input
                  type="text"
                  placeholder="Ej: Cra 15 # 85 - 20, Apto 402, Bogotá"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '12px',
                    border: '1.5px solid rgba(15,42,74,0.12)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '13px',
                    fontWeight: 600,
                    outline: 'none',
                    fontFamily: '"Montserrat", sans-serif',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* CONTACTO DE EMERGENCIA */}
            <div style={{ borderTop: '1px dashed rgba(0,112,243,0.15)', paddingTop: '12px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '10px' }}>
                🆘 Contacto de Emergencia
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '14px' }}>
                <div>
                  <input
                    type="text"
                    placeholder="Nombre completo (ej: María Gómez - Mamá)"
                    value={form.emergencyContactName}
                    onChange={e => setForm({ ...form, emergencyContactName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '12px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder="Teléfono (ej: +57 310 123 4567)"
                    value={form.emergencyContactPhone}
                    onChange={e => setForm({ ...form, emergencyContactPhone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '1.5px solid rgba(15,42,74,0.12)',
                      background: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '12px',
                      fontWeight: 600,
                      outline: 'none',
                      fontFamily: '"Montserrat", sans-serif',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

          </div>

          {/* BIOGRAFÍA / BREVE DESCRIPCIÓN */}
          <div>
            <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '6px' }}>Biografía o Presentación</label>
            <textarea
              rows={2}
              placeholder="Cuéntanos un poco sobre ti, tu rol creativo o intereses..."
              value={form.bio}
              onChange={e => setForm({ ...form, bio: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '12px',
                border: '1.5px solid rgba(15,42,74,0.12)',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '12px',
                fontWeight: 600,
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                boxSizing: 'border-box',
                resize: 'none'
              }}
            />
          </div>

          {/* BOTONES DE ACCIÓN */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '6px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '11px 20px',
                borderRadius: '12px',
                border: '1.5px solid rgba(15,42,74,0.15)',
                background: 'rgba(255,255,255,0.85)',
                color: '#0f2a4a',
                fontWeight: 800,
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: '"Montserrat", sans-serif'
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || uploadingAvatar}
              style={{
                padding: '11px 26px',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '13px',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(0, 112, 243, 0.25)',
                fontFamily: '"Montserrat", sans-serif'
              }}
            >
              {saving ? 'Guardando...' : 'Guardar Mi Perfil'}
            </button>
          </div>

        </form>

      </div>
    </div>
  );
};
export default ProfileEditModal;
